import { UpgradeSubscriptionModal } from '@components'

import { Button, ButtonsGroup, useModal } from '@billy/design-system'
import React, { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { CustomerModal } from '@features/contacts/elements/CustomerModal'
import { CustomerModalSubmitActionType } from '@features/contacts/enums/customerModalSubmitActionType'

import { queryClient } from '../../../../../../config/queryClient'
import { ModalId } from '../../../../../../enums/modalId'
import { QueryKeys } from '../../../../../../enums/queryKeys'
import { TrackingContext } from '../../../../../../enums/trackingContext'
import { useOrganizationSource, useSegment, useSubscriptionLimits } from '../../../../../../hooks'
import { useContactsEmberConnection } from '../../../../context/contactsEmberConnectionContext'
import { useContactType } from '../../contexts/contactTypeContext'
import { ContactsActions } from '../ContactsActions'
import { ContactsImportModal } from '../ContactsImportModal'

const upgradeModalId = ModalId.UpgradeSubscriptionModal
const customerModalId = ModalId.CustomerModal
const importModalId = ModalId.ContactsImportModal

export const ContactsTableSideActions = (): ReactElement => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const { open: openUpgradeModal } = useModal(upgradeModalId)
  const { open: openCustomerModal } = useModal(customerModalId)
  const { hasExceededContactsLimit, isLoading } = useSubscriptionLimits()
  const { createNewContact } = useContactsEmberConnection()
  const { isSourceBilly } = useOrganizationSource()
  const { isCustomer, isSupplier } = useContactType()

  const handleCreateContactClick = useCallback(() => {
    if (isCustomer) {
      track('XXX - Invoicing Success - Customers List - Create contact button clicked')
    }

    if (isCustomer && hasExceededContactsLimit) {
      openUpgradeModal()
      return
    }

    if (isSupplier || isSourceBilly) {
      createNewContact({ isCustomer, isSupplier })
      return
    }

    openCustomerModal()
  }, [
    createNewContact,
    hasExceededContactsLimit,
    isCustomer,
    isSourceBilly,
    isSupplier,
    openCustomerModal,
    openUpgradeModal,
    track,
  ])

  const handleCustomerCreation = useCallback(() => {
    queryClient.invalidateQueries(QueryKeys.ContactsAndContactPersons)
  }, [])

  return (
    <ButtonsGroup>
      <Button disabled={isLoading} icon="circlePlus" onClick={handleCreateContactClick}>
        {t('contacts.header.create_contact')}
      </Button>
      <ContactsActions />
      <UpgradeSubscriptionModal id={upgradeModalId} trackingContext={TrackingContext.CreateCustomer} />
      {isCustomer && (
        <CustomerModal
          id={customerModalId}
          onSubmit={handleCustomerCreation}
          size="s"
          submitActionType={CustomerModalSubmitActionType.Create}
        />
      )}
      <ContactsImportModal id={importModalId} />
    </ButtonsGroup>
  )
}
